import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  exportProvinceAovPerCarton,
  useProvinceAovPerCartonList,
  useUpdateProvinceAovPerCarton,
} from '@/hooks/queries/credit/useProvinceAovPerCarton'
import { UpdateProvinceAovPerCarton } from '@repo/backend/credit'
import { PROVINCE_CODES, type ProvinceCode } from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ProvinceAovDataTable, { ProvinceAovRow } from './-components/ProvinceAovDataTable'
import { UploadProvinceAovDataDialog } from './-components/UploadProvinceAovDataDialog'

export const Route = createFileRoute('/_auth/_modules/credit/operations/province-aov-per-carton/')({
  component: RouteComponent,
})

const normalizeAov = (value: string) =>
  value === '' || isNaN(Number(value)) ? value : String(Number(value))

function RouteComponent() {
  const { t } = useTranslation('credit')
  const { permissions } = usePermissions()
  const canEdit = hasPermissions(permissions, 'credit_province_aov_edit')

  const [editedValues, setEditedValues] = useState<Map<string, string>>(new Map())

  const { data, isPending, error } = useProvinceAovPerCartonList()

  const { mutate: updateProvinceAov, isPending: isSaving } = useUpdateProvinceAovPerCarton({
    handleOnSuccess: () => {
      setEditedValues(new Map())
    },
  })

  const persistedByProvince = new Map((data?.results ?? []).map((row) => [row.provinceCode, row]))
  const results: ProvinceAovRow[] = PROVINCE_CODES.map((provinceCode) => ({
    provinceCode,
    averageOrderValue: persistedByProvince.get(provinceCode)?.averageOrderValue ?? null,
  }))

  const originalByProvince = new Map(results.map((row) => [row.provinceCode, row]))

  const handleValueChange = useCallback(
    (provinceCode: ProvinceCode, value: string) => {
      // Clamp negatives to 0, but keep an empty field empty (unset AOV).
      const sanitized = value && Number(value) < 0 ? '0' : value

      setEditedValues((prev) => {
        const newMap = new Map(prev)
        const original = originalByProvince.get(provinceCode)

        if (
          original &&
          normalizeAov(sanitized) === normalizeAov(original.averageOrderValue ?? '')
        ) {
          newMap.delete(provinceCode)
          return newMap
        }

        newMap.set(provinceCode, sanitized)
        return newMap
      })
    },
    [originalByProvince],
  )

  const getDisplayValue = useCallback(
    (row: ProvinceAovRow): string =>
      editedValues.get(row.provinceCode) ?? row.averageOrderValue ?? '',
    [editedValues],
  )

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return

    const updates: UpdateProvinceAovPerCarton['updates'] = []

    editedValues.forEach((averageOrderValue, provinceCode) => {
      updates.push({ provinceCode, averageOrderValue })
    })

    updateProvinceAov({ updates })
  }

  const areChangesPending = editedValues.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread className="items-center">
        <PageHeader>{t('provinceAovPerCarton.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            label={t('provinceAovPerCarton.export')}
            getCsvExport={() => exportProvinceAovPerCarton()}
            rowCount={results.length}
          />
          <PermissionsGuard required={['credit_province_aov_edit']}>
            <UploadProvinceAovDataDialog />
          </PermissionsGuard>
        </HStack>
      </Spread>

      <ProvinceAovDataTable
        data={results}
        isPending={isPending}
        error={error}
        editedValues={editedValues}
        canEdit={canEdit}
        getDisplayValue={getDisplayValue}
        onValueChange={handleValueChange}
      />

      <PermissionsGuard required={['credit_province_aov_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
